import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./BecomePartner.css";

import bgImg from "../../../../assets/images/home/becamePartner/bg.jpg";
import arrowIcon from "../../../../assets/icons/handshake.svg";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackBecomePartner = {
  tag: "PARTNERSHIP",
  title: "Become a Partner",
  description:
    "Start your next project with us. Connect with IRQGYP, explore partnership opportunities to innovate, grow, and create mutually beneficial solutions.",
  buttonText: "Become a partner",
  buttonLink: "/contactus",
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalescePartnerPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return tryParseJsonObject(raw) || {};
  }
  if (typeof raw !== "object") return null;

  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const titleFromPayload = (c) => {
  const joined = [c?.titleLine1, c?.titleLine2].filter((x) => typeof x === "string" && x.trim()).join(" ").trim();
  return pickString(c?.title, c?.heading, c?.headline, joined);
};

const descriptionFromPayload = (c) =>
  pickString(c?.description, c?.body, c?.text, c?.summary, c?.content, c?.copy);

const getBackgroundCandidates = (c) => {
  if (!c || typeof c !== "object") return [];
  return [
    c.backgroundImage,
    c.bgImage,
    c.background,
    c.backgroundUrl,
    c.heroImage,
    c.image,
    c.bannerImage,
    c.mediaUrl,
    c.MediaUrl,
  ].filter((x) => x != null && x !== "");
};

const resolveFirstBackground = (candidates) => {
  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const hasMeaningfulPartnerPayload = (c) => {
  if (!c || typeof c !== "object") return false;
  if (pickString(c.tag, c.sectionTag)) return true;
  if (titleFromPayload(c)) return true;
  if (descriptionFromPayload(c)) return true;
  if (pickString(c.buttonText, c.buttonLink, c.ctaText, c.ctaLink)) return true;
  return Boolean(resolveFirstBackground(getBackgroundCandidates(c)));
};

const BecomePartner = ({ content }) => {
  const navigate = useNavigate();

  const { partner, backgroundUrl } = useMemo(() => {
    const payload = coalescePartnerPayload(content);
    if (!hasMeaningfulPartnerPayload(payload) || !payload) {
      return { partner: fallbackBecomePartner, backgroundUrl: "" };
    }

    const title = titleFromPayload(payload) || fallbackBecomePartner.title;
    const partner = {
      tag: pickString(payload.tag, payload.sectionTag) || fallbackBecomePartner.tag,
      title,
      description: descriptionFromPayload(payload) || fallbackBecomePartner.description,
      buttonText:
        pickString(payload.buttonText, payload.ctaText, payload.buttonLabel) || fallbackBecomePartner.buttonText,
      buttonLink: pickString(payload.buttonLink, payload.ctaLink, payload.link) || fallbackBecomePartner.buttonLink,
    };

    const bg = resolveFirstBackground(getBackgroundCandidates(payload));

    return { partner, backgroundUrl: bg };
  }, [content]);

  const bgCssUrl = backgroundUrl || bgImg;

  return (
    <div className="partner" style={{ backgroundImage: `url(${bgCssUrl})` }}>
      <div className="partner-overlay" aria-hidden="true" />

      <div className="partner-content">
        <span className="partner-tag">{partner.tag}</span>

        <h2>{partner.title}</h2>

        <p>{partner.description}</p>

        <button className="buttongyp" type="button" onClick={() => navigate(partner.buttonLink)}>
          {partner.buttonText}
          <img src={arrowIcon} alt="" />
        </button>
      </div>
    </div>
  );
};

export default BecomePartner;
